import { useCallback } from "react";
import i18n from "@/lib/i18n";

import {
  AGENT_CATALOG_BACKGROUND_STALE_MS,
  type Agent,
  type AgentRefreshMode,
} from "@/hooks/use-agents";
import type {
  LeaderKeyPickerItem,
  LeaderKeyPickerLoadResult,
} from "@/hooks/use-leader-keys";
import type { Project } from "@/lib/project-types";
import { groupByDate, type Session } from "@/lib/session";

/** Refresh stale shell data without making a leader picker wait for it. */
export const LEADER_PICKER_STALE_MS = 45_000;

export interface LeaderKeyPickerSources {
  agents: readonly Agent[];
  agentCatalogLoaded: boolean;
  agentCatalogUpdatedAt: number | null;
  agentCatalogError: string | null;
  refreshAgents: (mode?: AgentRefreshMode) => Promise<Agent[] | null>;

  agentIds: readonly string[];
  agentPinsLoaded: boolean;
  agentPinsUpdatedAt: number | null;
  agentPinsLoadError: string | null;
  refreshAgentPins: () => Promise<string[] | null>;

  projects: readonly Project[];
  projectsLoaded: boolean;
  projectsUpdatedAt: number | null;
  projectsListError: string | null;
  refreshProjects: () => Promise<Project[] | null>;

  /** The sidebar's flat, root-level session snapshot. */
  sessions: readonly Session[];
  sessionTodayLabel: string;
  sessionYesterdayLabel: string;
  sessionLocale: string;
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

/** Match the former accessible-agents response parser: teams are never picks. */
export function readLeaderAgentItems(agents: readonly Agent[]): LeaderKeyPickerItem[] {
  const seenIds = new Set<string>();
  const items: LeaderKeyPickerItem[] = [];
  for (const agent of agents) {
    if (agent.type === "team") continue;
    const id = nonEmptyString(agent.id);
    if (!id || seenIds.has(id)) continue;
    seenIds.add(id);
    items.push({ id, label: nonEmptyString(agent.name) ?? id });
  }
  return items;
}

function readPinnedAgentIds(agentIds: readonly string[]): string[] {
  const seenIds = new Set<string>();
  const ids: string[] = [];
  for (const value of agentIds) {
    const id = nonEmptyString(value);
    if (!id || seenIds.has(id)) continue;
    seenIds.add(id);
    ids.push(id);
  }
  return ids;
}

export function readPinnedProjectItems(projects: readonly Project[]): LeaderKeyPickerItem[] {
  const seenIds = new Set<string>();
  const items: LeaderKeyPickerItem[] = [];
  for (const project of projects) {
    if (typeof project.is_pinned !== "boolean") {
      throw new Error(i18n.t("chat:leaderKeys.errors.invalidProjects"));
    }
    if (!project.is_pinned) continue;
    const id = nonEmptyString(project.id);
    if (!id || seenIds.has(id)) continue;
    seenIds.add(id);
    items.push({ id, label: nonEmptyString(project.name) ?? id });
  }
  return items;
}

/**
 * Mirror the sidebar's root-level, date-grouped session order. Child sessions
 * are lazy-loaded only by SessionSidebar and intentionally are not included.
 */
export function readLeaderSessionItems(
  sessions: readonly Session[],
  todayLabel: string,
  yesterdayLabel: string,
  locale: string,
): LeaderKeyPickerItem[] {
  const seenIds = new Set<string>();
  const items: LeaderKeyPickerItem[] = [];
  const flatSessions = groupByDate(
    sessions,
    todayLabel,
    yesterdayLabel,
    locale,
  ).flatMap((group) => group.items);

  for (const session of flatSessions) {
    const id = nonEmptyString(session.id);
    if (!id || seenIds.has(id)) continue;
    seenIds.add(id);
    items.push({ id, label: nonEmptyString(session.title) ?? id });
  }

  return items;
}

function isStale(updatedAt: number | null, staleMs = LEADER_PICKER_STALE_MS): boolean {
  return updatedAt === null || Date.now() - updatedAt >= staleMs;
}

/**
 * Adapt shell-owned data to the asynchronous leader-picker contract.
 *
 * A ready snapshot resolves immediately and only starts a stale refresh in
 * the background. If shell warm-up is still running (or a previous attempt
 * failed), the first picker open waits for the existing shared refresh and
 * retains the indicator's loading/unavailable behavior.
 */
export function useLeaderKeyPickers({
  agents,
  agentCatalogLoaded,
  agentCatalogUpdatedAt,
  agentCatalogError,
  refreshAgents,
  agentIds,
  agentPinsLoaded,
  agentPinsUpdatedAt,
  agentPinsLoadError,
  refreshAgentPins,
  projects,
  projectsLoaded,
  projectsUpdatedAt,
  projectsListError,
  refreshProjects,
  sessions,
  sessionTodayLabel,
  sessionYesterdayLabel,
  sessionLocale,
}: LeaderKeyPickerSources) {
  const loadAgents = useCallback(async (): Promise<LeaderKeyPickerLoadResult> => {
    const hasAgentSnapshot = agentCatalogLoaded && !agentCatalogError;
    const hasPinSnapshot = agentPinsLoaded && !agentPinsLoadError;

    if (hasAgentSnapshot && hasPinSnapshot) {
      const result = {
        items: readLeaderAgentItems(agents),
        pinnedItemIds: readPinnedAgentIds(agentIds),
      };
      const backgroundRefresh = isStale(
        agentCatalogUpdatedAt,
        AGENT_CATALOG_BACKGROUND_STALE_MS,
      )
        ? refreshAgents("background").then((nextAgents) => (
            nextAgents === null
              ? null
              : {
                  items: readLeaderAgentItems(nextAgents),
                  pinnedItemIds: readPinnedAgentIds(agentIds),
                }
          ))
        : undefined;
      if (isStale(agentPinsUpdatedAt)) void refreshAgentPins();
      return backgroundRefresh ? { ...result, backgroundRefresh } : result;
    }

    // The hooks deduplicate an in-flight warm-up request, so this awaits the
    // sidebar/shell request rather than issuing a picker-specific fetch.
    const nextAgents = hasAgentSnapshot ? agents : await refreshAgents();
    const nextAgentIds = hasPinSnapshot ? agentIds : await refreshAgentPins();
    if (nextAgents === null || nextAgentIds === null) {
      throw new Error(i18n.t("chat:leaderKeys.errors.agentsUnavailable"));
    }
    return {
      items: readLeaderAgentItems(nextAgents),
      pinnedItemIds: readPinnedAgentIds(nextAgentIds),
    };
  }, [
    agentCatalogError,
    agentCatalogLoaded,
    agentCatalogUpdatedAt,
    agentIds,
    agentPinsLoadError,
    agentPinsLoaded,
    agentPinsUpdatedAt,
    agents,
    refreshAgentPins,
    refreshAgents,
  ]);

  const loadPinnedProjects = useCallback(async (): Promise<LeaderKeyPickerLoadResult> => {
    const hasProjectSnapshot = projectsLoaded && !projectsListError;
    if (hasProjectSnapshot) {
      const result = { items: readPinnedProjectItems(projects) };
      if (isStale(projectsUpdatedAt)) void refreshProjects();
      return result;
    }

    const nextProjects = await refreshProjects();
    if (nextProjects === null) {
      throw new Error(i18n.t("chat:leaderKeys.errors.projectsUnavailable"));
    }
    return { items: readPinnedProjectItems(nextProjects) };
  }, [
    projects,
    projectsListError,
    projectsLoaded,
    projectsUpdatedAt,
    refreshProjects,
  ]);

  // Session data is owned by useSessions() and is already the same flat,
  // root-level snapshot rendered by SessionSidebar. Resolve it directly: a
  // leader picker must never initiate a session-list refresh of its own.
  const loadSessions = useCallback(async (): Promise<LeaderKeyPickerLoadResult> => ({
    items: readLeaderSessionItems(
      sessions,
      sessionTodayLabel,
      sessionYesterdayLabel,
      sessionLocale,
    ),
  }), [sessions, sessionTodayLabel, sessionYesterdayLabel, sessionLocale]);

  return { loadAgents, loadPinnedProjects, loadSessions };
}
